import { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Checkbox } from "@/components/ui/checkbox";
import { useAuth } from "@/contexts/AuthContext";
import API from "@/lib/api";
import { toast } from "@/lib/notify";
import { BarChart3, Download, Filter, CheckCircle2, Send, ShieldCheck, XCircle, Zap, Eye, SendHorizonal } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { EmailConfirmDialog } from "@/components/EmailConfirmDialog";
import { Pagination, paginate } from "@/components/Pagination";

// Title-case labels for the canonical reporting-workflow status values.
const STATUS_LABELS = {
  prepared: "Prepared",
  reviewed: "Reviewed",
  pending_approval: "Pending Approval",
  approved: "Approved",
  rejected: "Rejected",
  released: "Released",
};

const STATUS_BADGE_CLASS = {
  prepared: "text-amber-400 border-amber-400/30",
  reviewed: "text-sky-400 border-sky-400/30",
  pending_approval: "text-indigo-400 border-indigo-400/30",
  approved: "text-blue-400 border-blue-400/30",
  rejected: "text-red-400 border-red-400/30",
  released: "text-emerald-400 border-emerald-400/30",
};

export default function ReportingPage() {
  const { user } = useAuth();
  const { fundId: portalFundId } = useParams();
  const [funds, setFunds] = useState([]);
  const [selectedFund, setSelectedFund] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reportLoading, setReportLoading] = useState(false);
  const [reportData, setReportData] = useState({ rows: [], periods: [] });
  const [filterInvestor, setFilterInvestor] = useState("all");
  const [filterPeriod, setFilterPeriod] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [investors, setInvestors] = useState([]);

  // Selection state for selective release
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [reportPage, setReportPage] = useState(1);
  const [reportPageSize, setReportPageSize] = useState(50);

  // Dialog states
  const [releaseEmailConfirm, setReleaseEmailConfirm] = useState(false);
  const [singleReleaseConfirm, setSingleReleaseConfirm] = useState(null); // holds txnId
  const [selectedReleaseConfirm, setSelectedReleaseConfirm] = useState(false);
  const [rejectDialog, setRejectDialog] = useState({ open: false, txnId: null, reason: "" });

  const isSA = user?.role === "super_admin";
  const isFM = user?.role === "admin";

  const loadFunds = useCallback(async () => {
    try {
      if (portalFundId) {
        const { data } = await API.get(`/funds/${portalFundId}`);
        setFunds([data]);
        setSelectedFund(data);
      } else if (user?.role === "admin") {
        const fundIds = user?.fund_ids || [];
        if (fundIds.length > 0) {
          const fundPromises = fundIds.map(id => API.get(`/funds/${id}`));
          const results = await Promise.all(fundPromises);
          const allFunds = results.map(r => r.data);
          setFunds(allFunds);
          setSelectedFund(allFunds.find(f => f.status === "active") || allFunds[0]);
        }
      } else {
        const { data } = await API.get("/funds");
        setFunds(data.filter(f => f.status === "active" || f.status === "draft"));
        if (data.length > 0) setSelectedFund(data.find(f => f.status === "active") || data[0]);
      }
    } catch { toast.error("Failed to load funds"); }
    finally { setLoading(false); }
  }, [user, portalFundId]);

  useEffect(() => { loadFunds(); }, [loadFunds]);

  const loadReport = useCallback(async () => {
    if (!selectedFund) return;
    setReportLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterInvestor !== "all") params.append("investor_id", filterInvestor);
      if (filterPeriod !== "all") params.append("period_id", filterPeriod);
      const [reportRes, invRes] = await Promise.all([
        API.get(`/funds/${selectedFund.id}/roll-on-report?${params}`),
        API.get(`/investors?fund_id=${selectedFund.id}`),
      ]);
      setReportData(reportRes.data);
      setInvestors(invRes.data);
    } catch { toast.error("Failed to load report"); }
    finally { setReportLoading(false); }
  }, [selectedFund, filterInvestor, filterPeriod]);

  const sharesApplicable = reportData.shares_applicable || selectedFund?.shares_applicable;

  useEffect(() => { loadReport(); }, [loadReport]);

  const releaseAll = async () => {
    if (!selectedFund) return;
    try {
      const params = new URLSearchParams();
      if (filterPeriod !== "all") params.append("period_id", filterPeriod);
      const { data } = await API.patch(`/funds/${selectedFund.id}/performance-transactions/release-all?${params}`);
      toast.success(data.message || "All entries released");
      loadReport();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to release"); }
  };

  const approveAll = async () => {
    if (!selectedFund) return;
    try {
      const params = new URLSearchParams();
      if (filterPeriod !== "all") params.append("period_id", filterPeriod);
      const { data } = await API.patch(`/funds/${selectedFund.id}/performance-transactions/approve-all?${params}`);
      toast.success(`${data.count} entries approved`);
      loadReport();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to approve"); }
  };

  // Phase 6.5 workflow actions: Review (Admin), Submit (Admin), Approve/Reject (FM), Re-Submit (Admin), Release (Admin)
  const reviewSingle = async (txnId) => {
    if (!selectedFund) return;
    try {
      await API.patch(`/funds/${selectedFund.id}/performance-transactions/${txnId}/review`);
      toast.success("Marked as Reviewed");
      loadReport();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to review"); }
  };

  const reviewAll = async () => {
    if (!selectedFund) return;
    try {
      const params = new URLSearchParams();
      if (filterPeriod !== "all") params.append("period_id", filterPeriod);
      const { data } = await API.patch(`/funds/${selectedFund.id}/performance-transactions/review-all?${params}`);
      toast.success(`${data.count} entries reviewed`);
      loadReport();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to review"); }
  };

  const submitSingle = async (txnId) => {
    if (!selectedFund) return;
    try {
      const { data } = await API.patch(`/funds/${selectedFund.id}/performance-transactions/${txnId}/submit`);
      toast.success(data.message || "Submitted");
      loadReport();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to submit"); }
  };

  const submitAll = async () => {
    if (!selectedFund) return;
    try {
      const params = new URLSearchParams();
      if (filterPeriod !== "all") params.append("period_id", filterPeriod);
      const { data } = await API.patch(`/funds/${selectedFund.id}/performance-transactions/submit-all?${params}`);
      toast.success(data.message || `${data.count} entries submitted`);
      loadReport();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to submit"); }
  };

  const resubmitSingle = async (txnId) => {
    if (!selectedFund) return;
    try {
      await API.patch(`/funds/${selectedFund.id}/performance-transactions/${txnId}/resubmit`);
      toast.success("Re-submitted for approval");
      loadReport();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to re-submit"); }
  };

  const approveSingle = async (txnId) => {
    if (!selectedFund) return;
    try {
      await API.patch(`/funds/${selectedFund.id}/performance-transactions/${txnId}/approve`);
      toast.success("Entry approved");
      loadReport();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to approve"); }
  };

  const resendStatementEmail = async (investorId, periodId, investorName) => {
    try {
      await API.post(`/investors/${investorId}/resend-statement/${periodId}`);
      toast.success(`Statement email resent to ${investorName}`);
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to resend email"); }
  };

  // Reject opens a dialog so the FM can supply the mandatory rejection reason.
  const openRejectDialog = (txnId) => setRejectDialog({ open: true, txnId, reason: "" });

  const submitRejection = async () => {
    if (!rejectDialog.txnId) return;
    const reason = (rejectDialog.reason || "").trim();
    if (!reason) {
      toast.error("Please provide a rejection reason");
      return;
    }
    try {
      await API.patch(
        `/funds/${selectedFund.id}/performance-transactions/${rejectDialog.txnId}/reject`,
        { reason }
      );
      toast.success("Statement rejected");
      setRejectDialog({ open: false, txnId: null, reason: "" });
      loadReport();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to reject");
    }
  };

  const releaseSingle = async (txnId) => {
    if (!selectedFund) return;
    try {
      const { data } = await API.patch(`/funds/${selectedFund.id}/performance-transactions/${txnId}/release`);
      // Surface whether the investor email was actually queued so the user has a clear record.
      const investorRow = reportData.rows?.find(r => r.perf_txn_id === txnId);
      const inv = investorRow ? await fetchInvestorOptOut(investorRow.investor_id) : null;
      let msg = data.message || "Released";
      if (inv && inv.outgoing_email_enabled === false) msg += " (investor email opt-out — no notification sent)";
      else msg += " — investor notified by email";
      toast.success(msg);
      loadReport();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to release"); }
  };

  // Lightweight cache-less helper: returns just the email opt-out flag for an investor.
  const fetchInvestorOptOut = async (investorId) => {
    try {
      const { data } = await API.get(`/investors/${investorId}`);
      return { outgoing_email_enabled: data.outgoing_email_enabled };
    } catch { return null; }
  };

  const releaseSelected = async () => {
    if (!selectedFund || selectedRows.size === 0) return;
    try {
      const txnIds = [...selectedRows];
      let released = 0;
      for (const txnId of txnIds) {
        try {
          await API.patch(`/funds/${selectedFund.id}/performance-transactions/${txnId}/release`);
          released++;
        } catch { /* skip failures */ }
      }
      toast.success(`${released} statement(s) released to investors`);
      setSelectedRows(new Set());
      loadReport();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to release selected"); }
  };

  const exportCSV = () => {
    if (!reportData.rows.length) return;
    const headers = ["Period", "Fund", "Investor #", "Investor Name", "Offering", "Beginning Balance", "Additions", "Gross Income", "Mgmt Fee", "Incentive Fee", "Redemptions", "Ending Balance", "Rate of Return", "Status", "Investor Type"];
    const csvRows = [headers.join(",")];
    reportData.rows.forEach(r => {
      csvRows.push([
        r.period_label, `"${r.fund_name}"`, r.investor_number, `"${r.investor_name}"`,
        `"${r.class_description || ""}"`, r.beginning_balance, r.additions, r.total_income, r.management_fee,
        r.incentive_fee, r.redemptions, r.ending_balance, `${r.rate_of_return}%`,
        `"${STATUS_LABELS[r.status] || "Prepared"}"`, `"${r.entity_type || ""}"`,
      ].join(","));
    });
    const blob = new Blob([csvRows.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `roll_on_report_${selectedFund?.name || "fund"}.csv`; a.click();
  };

  // Selection helpers
  const filteredRows = (reportData.rows || []).filter(r => filterStatus === "all" || r.status === filterStatus);
  const releasableRows = filteredRows.filter(r => r.status !== "released" && r.perf_txn_id);

  const previewStatement = async (investorId, periodId) => {
    try {
      const response = await API.get(`/investors/${investorId}/statement/${periodId}?preview=1`, { responseType: "blob" });
      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to preview statement");
    }
  };
  const allReleasableSelected = releasableRows.length > 0 && releasableRows.every(r => selectedRows.has(r.perf_txn_id));
  const someSelected = selectedRows.size > 0;

  const toggleSelectAll = () => {
    if (allReleasableSelected) {
      setSelectedRows(new Set());
    } else {
      setSelectedRows(new Set(releasableRows.map(r => r.perf_txn_id)));
    }
  };

  const toggleRowSelect = (txnId) => {
    setSelectedRows(prev => {
      const next = new Set(prev);
      if (next.has(txnId)) next.delete(txnId);
      else next.add(txnId);
      return next;
    });
  };

  const fmt = (v) => `$${(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="space-y-5 animate-fade-in" data-testid="reporting-page">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Reporting</h1>
          {selectedFund && (
            <div className="flex items-center gap-2 mt-1.5">
              {(user?.role === "super_admin" || user?.role === "admin") && funds.length > 1 ? (
                <Select value={selectedFund.id} onValueChange={(v) => setSelectedFund(funds.find(f => f.id === v))}>
                  <SelectTrigger className="bg-transparent border-none h-auto p-0 text-muted-foreground text-sm font-medium gap-1 hover:text-foreground transition-colors [&>svg]:w-3.5 [&>svg]:h-3.5" data-testid="report-fund-selector">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-card border-border">
                    {funds.map(f => <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              ) : (
                <span className="text-muted-foreground text-sm">{selectedFund.name}</span>
              )}
              <Badge variant="outline" className="text-[9px] uppercase tracking-widest font-semibold px-2 py-0">{selectedFund.reporting_frequency}</Badge>
            </div>
          )}
        </div>
        <div className="flex items-center gap-2 text-sm">
          <div className="flex items-center gap-1.5 text-muted-foreground">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 inline-block" /> Periods
            <span className="text-foreground font-semibold">{reportData.periods?.length || 0}</span>
          </div>
          <div className="flex items-center gap-1.5 text-muted-foreground">
            <span className="w-1.5 h-1.5 rounded-full bg-blue-400 inline-block" /> Rows
            <span className="text-foreground font-semibold">{reportData.rows?.length || 0}</span>
          </div>
        </div>
      </div>

      {selectedFund && (
        <>
          {/* Toolbar */}
          <div className="flex items-center justify-between gap-3 border-b border-border pb-3">
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <Filter className="w-3.5 h-3.5" />
              </div>
              <Select value={filterInvestor} onValueChange={setFilterInvestor}>
                <SelectTrigger className="bg-secondary/40 border-border/50 h-8 text-xs w-44" data-testid="report-filter-investor"><SelectValue placeholder="All Investors" /></SelectTrigger>
                <SelectContent className="bg-card border-border">
                  <SelectItem value="all">All Investors</SelectItem>
                  {investors.map(i => <SelectItem key={i.id} value={i.id}>{i.name}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={filterPeriod} onValueChange={setFilterPeriod}>
                <SelectTrigger className="bg-secondary/40 border-border/50 h-8 text-xs w-36" data-testid="report-filter-period"><SelectValue placeholder="All Periods" /></SelectTrigger>
                <SelectContent className="bg-card border-border">
                  <SelectItem value="all">All Periods</SelectItem>
                  {reportData.periods?.map(p => <SelectItem key={p.id} value={p.id}>{p.label}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={filterStatus} onValueChange={(v) => { setFilterStatus(v); setReportPage(1); }}>
                <SelectTrigger className="bg-secondary/40 border-border/50 h-8 text-xs w-40" data-testid="report-filter-status"><SelectValue placeholder="All Statuses" /></SelectTrigger>
                <SelectContent className="bg-card border-border">
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="prepared">Prepared</SelectItem>
                  <SelectItem value="reviewed">Reviewed</SelectItem>
                  <SelectItem value="pending_approval">Pending Approval</SelectItem>
                  <SelectItem value="approved">Approved</SelectItem>
                  <SelectItem value="rejected">Rejected</SelectItem>
                  <SelectItem value="released">Released</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              {/* FM: Approve All pending_approval entries */}
              {isFM && filteredRows.some(r => r.status === "pending_approval") && (
                <Button variant="default" size="sm" className="h-8 text-xs bg-blue-600 hover:bg-blue-700" onClick={approveAll} data-testid="approve-all-btn">
                  <ShieldCheck className="w-3.5 h-3.5 mr-1.5" /> Approve All
                </Button>
              )}
              {/* SA: Review All prepared entries */}
              {isSA && filteredRows.some(r => r.status === "prepared") && (
                <Button variant="default" size="sm" className="h-8 text-xs bg-sky-600 hover:bg-sky-700" onClick={reviewAll} data-testid="review-all-btn">
                  <Eye className="w-3.5 h-3.5 mr-1.5" /> Review All
                </Button>
              )}
              {/* SA: Submit All reviewed entries */}
              {isSA && filteredRows.some(r => r.status === "reviewed") && (
                <Button variant="default" size="sm" className="h-8 text-xs bg-indigo-600 hover:bg-indigo-700" onClick={submitAll} data-testid="submit-all-btn">
                  <SendHorizonal className="w-3.5 h-3.5 mr-1.5" /> Submit All
                </Button>
              )}
              {/* SA: Release Selected — with confirmation */}
              {isSA && someSelected && (
                <Button variant="default" size="sm" className="h-8 text-xs bg-emerald-600 hover:bg-emerald-700" onClick={() => setSelectedReleaseConfirm(true)} data-testid="release-selected-btn">
                  <CheckCircle2 className="w-3.5 h-3.5 mr-1.5" /> Release Selected ({selectedRows.size})
                </Button>
              )}
              {/* SA: Release All approved entries */}
              {isSA && filteredRows.some(r => r.status === "approved") && (
                <Button variant="default" size="sm" className="h-8 text-xs bg-emerald-600 hover:bg-emerald-700" onClick={() => setReleaseEmailConfirm(true)} data-testid="release-all-btn">
                  <Zap className="w-3.5 h-3.5 mr-1.5" /> Release All Approved
                </Button>
              )}
              <Button variant="secondary" size="sm" className="h-8 text-xs border border-border" onClick={exportCSV} disabled={!reportData.rows?.length} data-testid="export-csv-btn">
                <Download className="w-3.5 h-3.5 mr-1.5" /> Export CSV
              </Button>
            </div>
          </div>

          {/* Report Table */}
          <Card className="overflow-hidden" data-testid="roll-on-table">
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-secondary/20 hover:bg-secondary/20 border-b border-border/50">
                      {isSA && (
                        <TableHead className="w-[40px] pl-3">
                          <Checkbox
                            checked={allReleasableSelected}
                            onCheckedChange={toggleSelectAll}
                            data-testid="select-all-checkbox"
                            aria-label="Select all"
                          />
                        </TableHead>
                      )}
                      <TableHead className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold pl-4 sticky left-0 bg-card z-10 min-w-[100px]">Period</TableHead>
                      <TableHead className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold sticky left-[100px] bg-card z-10 min-w-[140px]">Investor</TableHead>
                      <TableHead className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold min-w-[100px]">Offering</TableHead>
                      <TableHead className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold text-right min-w-[120px]">Beginning Bal</TableHead>
                      <TableHead className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold text-right min-w-[100px]">Additions</TableHead>
                      <TableHead className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold text-right min-w-[100px]">Gross Income</TableHead>
                      <TableHead className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold text-right min-w-[100px]">Mgmt Fee</TableHead>
                      <TableHead className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold text-right min-w-[100px]">Incentive Fee</TableHead>
                      <TableHead className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold text-right min-w-[100px]">Redemptions</TableHead>
                      <TableHead className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold text-right min-w-[120px]">Ending Bal</TableHead>
                      <TableHead className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold text-right min-w-[80px]">RoR</TableHead>
                      {sharesApplicable && <>
                        <TableHead className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold text-right min-w-[80px]">Beg Shares</TableHead>
                        <TableHead className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold text-right min-w-[80px]">End Shares</TableHead>
                        <TableHead className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold text-right min-w-[80px]">NAV/Share</TableHead>
                      </>}
                      <TableHead className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold min-w-[100px]">Status</TableHead>
                      <TableHead className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold min-w-[100px] pr-4">Investor Type</TableHead>
                      {(isSA || isFM) && (
                        <TableHead className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold min-w-[80px] pr-4">Actions</TableHead>
                      )}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {reportLoading ? [1,2,3].map(i => (
                      <TableRow key={i}><TableCell colSpan={isSA ? 18 : 17} className="pl-4"><div className="h-8 bg-secondary/30 rounded animate-pulse" /></TableCell></TableRow>
                    )) : filteredRows.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={isSA ? 18 : 17} className="text-center py-16 text-muted-foreground text-sm">
                          <BarChart3 className="w-8 h-8 mx-auto mb-2 opacity-30" />
                          No report data available. Upload performance transactions to generate reports.
                        </TableCell>
                      </TableRow>
                    ) : (() => {
                      const { paginatedItems: pagedRows } = paginate(filteredRows, reportPage, reportPageSize);
                      return pagedRows.map((r, idx) => {
                        const globalIdx = (reportPage - 1) * reportPageSize + idx;
                        return (
                      <TableRow key={globalIdx} className="group" data-testid={`report-row-${globalIdx}`}>
                        {isSA && (
                          <TableCell className="w-[40px] pl-3">
                            {r.status !== "released" && r.perf_txn_id ? (
                              <Checkbox
                                checked={selectedRows.has(r.perf_txn_id)}
                                onCheckedChange={() => toggleRowSelect(r.perf_txn_id)}
                                data-testid={`select-row-${globalIdx}`}
                                aria-label={`Select ${r.investor_name}`}
                              />
                            ) : (
                              <span className="w-4 h-4 inline-block" />
                            )}
                          </TableCell>
                        )}
                        <TableCell className="pl-4 text-sm font-medium sticky left-0 bg-card z-10">{r.period_label}</TableCell>
                        <TableCell className="text-sm sticky left-[100px] bg-card z-10">{r.investor_name}</TableCell>
                        <TableCell className="text-xs text-muted-foreground">{r.class_description || "-"}</TableCell>
                        <TableCell className="text-right text-sm tabular-nums">{fmt(r.beginning_balance)}</TableCell>
                        <TableCell className="text-right text-sm tabular-nums">{r.additions > 0 ? <span className="text-emerald-400">{fmt(r.additions)}</span> : <span className="text-muted-foreground/40">-</span>}</TableCell>
                        <TableCell className="text-right text-sm tabular-nums">{r.total_income > 0 ? <span className="text-emerald-400">{fmt(r.total_income)}</span> : <span className="text-muted-foreground/40">-</span>}</TableCell>
                        <TableCell className="text-right text-sm tabular-nums">{r.management_fee > 0 ? <span className="text-amber-400">{fmt(r.management_fee)}</span> : <span className="text-muted-foreground/40">-</span>}</TableCell>
                        <TableCell className="text-right text-sm tabular-nums">{r.incentive_fee > 0 ? <span className="text-violet-400">{fmt(r.incentive_fee)}</span> : <span className="text-muted-foreground/40">-</span>}</TableCell>
                        <TableCell className="text-right text-sm tabular-nums">{r.redemptions > 0 ? <span className="text-red-400">{fmt(r.redemptions)}</span> : <span className="text-muted-foreground/40">-</span>}</TableCell>
                        <TableCell className="text-right text-sm tabular-nums font-semibold">{fmt(r.ending_balance)}</TableCell>
                        <TableCell className="text-right text-sm tabular-nums pr-4">{r.rate_of_return !== 0 ? <span className={r.rate_of_return >= 0 ? "text-emerald-400" : "text-red-400"}>{r.rate_of_return.toFixed(2)}%</span> : <span className="text-muted-foreground/40">-</span>}</TableCell>
                        {sharesApplicable && <>
                          <TableCell className="text-right text-sm tabular-nums text-muted-foreground">{r.beginning_shares ? r.beginning_shares.toLocaleString(undefined, {maximumFractionDigits:2}) : "-"}</TableCell>
                          <TableCell className="text-right text-sm tabular-nums font-medium">{r.ending_shares ? r.ending_shares.toLocaleString(undefined, {maximumFractionDigits:2}) : "-"}</TableCell>
                          <TableCell className="text-right text-sm tabular-nums text-muted-foreground">{r.nav_per_share ? fmt(r.nav_per_share) : "-"}</TableCell>
                        </>}
                        <TableCell className="text-xs">
                          <Badge variant="outline" className={`${STATUS_BADGE_CLASS[r.status] || "text-amber-400 border-amber-400/30"} text-[10px]`} data-testid={`status-${r.status || "prepared"}-${globalIdx}`}>
                            {STATUS_LABELS[r.status] || "Prepared"}
                          </Badge>
                          {r.status === "rejected" && r.rejection_reason && (
                            <p className="text-[10px] text-red-300 mt-0.5 max-w-[200px] truncate" title={r.rejection_reason}>
                              Reason: {r.rejection_reason}
                            </p>
                          )}
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground pr-4">{r.entity_type || "-"}</TableCell>
                        {(isSA || isFM) && (
                          <TableCell className="pr-4">
                            <div className="flex items-center gap-1">
                              {/* Preview statement — available for all rows with data */}
                              {r.perf_txn_id && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-7 text-[10px] text-muted-foreground hover:text-primary"
                                  onClick={() => previewStatement(r.investor_id, r.period_id)}
                                  data-testid={`preview-entry-${globalIdx}`}
                                >
                                  <Eye className="w-3 h-3 mr-1" /> Preview
                                </Button>
                              )}
                              {/* Admin (SA): Prepared → Review */}
                              {isSA && r.status === "prepared" && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-7 text-[10px] text-sky-400 hover:text-sky-300"
                                  onClick={() => reviewSingle(r.perf_txn_id)}
                                  data-testid={`review-entry-${globalIdx}`}
                                >
                                  <Eye className="w-3 h-3 mr-1" /> Review
                                </Button>
                              )}
                              {/* Admin (SA): Reviewed → Submit (auto-approves if FM toggle OFF) */}
                              {isSA && r.status === "reviewed" && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-7 text-[10px] text-indigo-400 hover:text-indigo-300"
                                  onClick={() => submitSingle(r.perf_txn_id)}
                                  data-testid={`submit-entry-${globalIdx}`}
                                >
                                  <SendHorizonal className="w-3 h-3 mr-1" /> Submit
                                </Button>
                              )}
                              {/* FM: Pending Approval → Approve / Reject */}
                              {isFM && r.status === "pending_approval" && (
                                <>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-7 text-[10px] text-blue-400 hover:text-blue-300"
                                    onClick={() => approveSingle(r.perf_txn_id)}
                                    data-testid={`approve-entry-${globalIdx}`}
                                  >
                                    <ShieldCheck className="w-3 h-3 mr-1" /> Approve
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-7 text-[10px] text-red-400 hover:text-red-300"
                                    onClick={() => openRejectDialog(r.perf_txn_id)}
                                    data-testid={`reject-entry-${globalIdx}`}
                                  >
                                    <XCircle className="w-3 h-3 mr-1" /> Reject
                                  </Button>
                                </>
                              )}
                              {/* FM sees approved badge for approved entries */}
                              {isFM && r.status === "approved" && (
                                <span className="text-[10px] text-blue-400">Awaiting Release</span>
                              )}
                              {/* Admin (SA): Rejected → Re-Submit (after editing in Transactions page) */}
                              {isSA && r.status === "rejected" && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-7 text-[10px] text-indigo-400 hover:text-indigo-300"
                                  onClick={() => resubmitSingle(r.perf_txn_id)}
                                  data-testid={`resubmit-entry-${globalIdx}`}
                                >
                                  <SendHorizonal className="w-3 h-3 mr-1" /> Re-Submit
                                </Button>
                              )}
                              {/* SA can release approved entries — with confirmation */}
                              {isSA && r.status === "approved" && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-7 text-[10px] text-emerald-400 hover:text-emerald-300"
                                  onClick={() => setSingleReleaseConfirm(r.perf_txn_id)}
                                  data-testid={`release-entry-${globalIdx}`}
                                >
                                  <CheckCircle2 className="w-3 h-3 mr-1" /> Release
                                </Button>
                              )}
                              {/* SA can resend email for released entries */}
                              {isSA && r.status === "released" && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-7 text-[10px] text-muted-foreground hover:text-primary"
                                  onClick={() => resendStatementEmail(r.investor_id, r.period_id, r.investor_name)}
                                  data-testid={`resend-email-${globalIdx}`}
                                >
                                  <Send className="w-3 h-3 mr-1" /> Resend
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        )}
                      </TableRow>
                    );
                      });
                    })()}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
          {(() => {
            const { totalPages, safePage } = paginate(filteredRows, reportPage, reportPageSize);
            return <Pagination currentPage={safePage} totalPages={totalPages} onPageChange={setReportPage} pageSize={reportPageSize} onPageSizeChange={(v) => { setReportPageSize(v); setReportPage(1); }} />;
          })()}
          <p className="text-xs text-muted-foreground">{filteredRows.length} row{filteredRows.length !== 1 ? "s" : ""}{filterStatus !== "all" ? ` (filtered)` : ""}</p>
        </>
      )}

      {!selectedFund && !loading && (
        <div className="flex flex-col items-center justify-center h-64 text-muted-foreground text-sm">No fund available</div>
      )}

      {/* Single Release Confirmation */}
      <ConfirmDialog
        open={!!singleReleaseConfirm}
        onOpenChange={(open) => { if (!open) setSingleReleaseConfirm(null); }}
        title="Release Statement"
        description="Are you sure you want to release this statement? Once released, the investor will be able to view this statement and an email notification will be sent."
        confirmLabel="Release"
        variant="default"
        onConfirm={() => { releaseSingle(singleReleaseConfirm); setSingleReleaseConfirm(null); }}
      />

      {/* Release Selected Confirmation */}
      <EmailConfirmDialog
        open={selectedReleaseConfirm}
        onOpenChange={setSelectedReleaseConfirm}
        title={`Release ${selectedRows.size} Selected Statement(s)`}
        description={`You are about to release ${selectedRows.size} selected statement(s) to investors. Email notifications will be sent to investors with active email settings. Do you want to proceed?`}
        onConfirm={releaseSelected}
      />

      {/* Release All Approved Confirmation */}
      <EmailConfirmDialog
        open={releaseEmailConfirm}
        onOpenChange={setReleaseEmailConfirm}
        title="Release All Approved Statements"
        description="This will release all approved statements to investors. Email notifications will be sent to investors with active email settings. Do you want to proceed?"
        onConfirm={releaseAll}
      />

      {/* Rejection Reason Dialog (FM only, mandatory reason per spec §9) */}
      <Dialog open={rejectDialog.open} onOpenChange={(open) => { if (!open) setRejectDialog({ open: false, txnId: null, reason: "" }); }}>
        <DialogContent className="sm:max-w-md" data-testid="reject-reason-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Reject Statement</DialogTitle>
            <DialogDescription>Please provide a reason for rejecting this statement. The Admin will see this when re-working it.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Textarea
              placeholder="Reason for rejection (required)"
              value={rejectDialog.reason}
              onChange={(e) => setRejectDialog(prev => ({ ...prev, reason: e.target.value }))}
              rows={4}
              data-testid="reject-reason-input"
              autoFocus
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectDialog({ open: false, txnId: null, reason: "" })} data-testid="reject-cancel-btn">Cancel</Button>
            <Button
              variant="destructive"
              onClick={submitRejection}
              disabled={!rejectDialog.reason.trim()}
              data-testid="reject-confirm-btn"
            >
              Submit Rejection
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
